import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Sparkles,
  Activity,
  Heart,
  CheckSquare,
  MessageSquare,
  Image as ImageIcon,
} from "lucide-react";
import { scopeChipMeta } from "../lib/scope-meta";
import { TeachingBanner } from "../components/TeachingBanner";
import { Breadcrumb } from "../components/Breadcrumb";
import { ClaimedFactsPanel } from "../components/ClaimedFactsPanel";
import { api, type Person } from "../lib/api";
import { useViewEmission } from "../lib/useAttention";

/** Wave 4 item 2: rich person dossier at /people/:id.
 *
 *  Everything Memso has woven about one person on a single canvas:
 *  a 12-month contact sparkline, the LLM relationship snapshot
 *  (qwen3:8b), sentiment trend facets, open commitments, recent
 *  conversations, and a media count. All reads are tolerant and
 *  active-view scoped server-side. */
interface Dossier {
  ok: boolean;
  person: {
    id: string;
    name: string;
    primary_scope: string | null;
    sub_scope: string | null;
    last_seen: string | null;
    strength: number | null;
  };
  snapshot: string;
  sparkline: Array<{ ym: string; n: number }>;
  sentiment: Array<Record<string, unknown>>;
  commitments: Array<{ id: string; title: string; status: string; due_at: string | null }>;
  conversations: Array<{ thread_key: string; summary_md: string; last_summarized_at: string }>;
  media_count: number;
}

export function PersonDossier() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<Dossier | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setData(null);
    setError(null);
    fetch(`/api/people/${encodeURIComponent(id)}/dossier`, {
      credentials: "same-origin",
    })
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(r.status === 404 ? "not found" : `HTTP ${r.status}`)),
      )
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const viewItems = useMemo(
    () =>
      (data?.conversations ?? []).map((c) => ({
        cluster: data?.person.id ?? id ?? null,
        itemKind: "conversation_summary",
        itemId: c.thread_key,
        context: { person_id: data?.person.id ?? id ?? null },
      })),
    [data, id],
  );
  useViewEmission("people", viewItems);

  if (error)
    return (
      <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-4">
        <Breadcrumb
          segments={[
            { label: "Explore", href: "/explore" },
            { label: "People", href: "/explore/people" },
          ]}
        />
        <div className="rounded-lg border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-900/30 px-4 py-3 text-sm text-rose-800 dark:text-rose-200">
          Could not load this person: {error}
        </div>
      </div>
    );
  if (!data)
    return (
      <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-10 text-sm text-slate-400">
        Loading…
      </div>
    );

  const chip = scopeChipMeta(data.person.primary_scope, data.person.sub_scope);
  const maxSpark = Math.max(1, ...data.sparkline.map((s) => s.n));

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6">
      <div className="lg:grid lg:gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
        <aside className="hidden lg:block self-start sticky top-6">
          <PeopleSidePane
            selectedId={id ?? null}
            onPick={(pid) => navigate(`/people/${pid}`)}
          />
        </aside>
        <section className="space-y-6 min-w-0">
      <Breadcrumb
        segments={[
          { label: "Explore", href: "/explore" },
          { label: "People", href: "/explore/people" },
          { label: data.person.name },
        ]}
      />
      <header className="flex items-start gap-4 flex-wrap">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-400 to-fuchsia-500 flex items-center justify-center text-white text-xl font-bold shadow-md flex-shrink-0">
          {data.person.name.slice(0, 1).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
            {data.person.name}
          </h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${chip.className}`}>
              {chip.label}
            </span>
            {data.person.last_seen && (
              <span className="text-xs text-slate-400">
                last woven {new Date(data.person.last_seen).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </header>

      <TeachingBanner id="person_dossier_overview" />

      {id && (
        <ClaimedFactsPanel kind="person" id={id} title="What Memso knows" />
      )}

      {data.snapshot && (
        <section className="glass rounded-2xl border border-violet-200/70 dark:border-violet-800/40 backdrop-blur bg-gradient-to-br from-violet-50/60 via-white/30 to-fuchsia-50/40 dark:from-violet-900/15 dark:via-slate-800/20 dark:to-fuchsia-900/10 p-5 sm:p-6">
          <div className="flex items-center gap-2 text-sm font-semibold text-violet-700 dark:text-violet-300 mb-2">
            <Sparkles className="w-4 h-4" />
            Relationship snapshot
          </div>
          <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
            {data.snapshot}
          </p>
        </section>
      )}

      <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100 mb-3">
          <Activity className="w-4 h-4 text-slate-400" />
          Contact over the last 12 months
        </h2>
        {data.sparkline.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No mentions in this window.</p>
        ) : (
          <div className="flex items-end gap-1 h-20">
            {data.sparkline.map((s) => (
              <div
                key={s.ym}
                title={`${s.ym}: ${s.n}`}
                className="flex-1 bg-sky-500/70 dark:bg-sky-400/60 rounded-t"
                style={{ height: `${Math.max(4, (s.n / maxSpark) * 100)}%` }}
              />
            ))}
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2.5">
            <CheckSquare className="w-4 h-4 text-slate-400" />
            Open commitments
          </h2>
          {data.commitments.length === 0 ? (
            <p className="text-xs text-slate-400 italic">None open.</p>
          ) : (
            <ul className="space-y-1.5">
              {data.commitments.map((c) => (
                <li
                  key={c.id}
                  className="text-sm text-slate-700 dark:text-slate-200 flex items-baseline justify-between gap-2"
                >
                  <span className="truncate">{c.title}</span>
                  {c.due_at && (
                    <span className="text-[11px] text-slate-400 flex-shrink-0">
                      {new Date(c.due_at).toLocaleDateString()}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2.5">
            <Heart className="w-4 h-4 text-slate-400" />
            Sentiment trend
          </h2>
          {data.sentiment.length === 0 ? (
            <p className="text-xs text-slate-400 italic">Not enough signal yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {data.sentiment.slice(0, 6).map((f, i) => (
                <li
                  key={i}
                  className="text-xs text-slate-600 dark:text-slate-300 truncate"
                >
                  {String(
                    f.summary ?? f.facet ?? f.label ?? f.sentiment ?? JSON.stringify(f),
                  ).slice(0, 120)}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2.5">
          <MessageSquare className="w-4 h-4 text-slate-400" />
          Recent conversations
          <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-400 font-normal">
            <ImageIcon className="w-3.5 h-3.5" />
            {data.media_count} media
          </span>
        </h2>
        {data.conversations.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No summarized threads yet.</p>
        ) : (
          <ul className="space-y-2.5">
            {data.conversations.map((c) => (
              <li key={c.thread_key} className="text-sm">
                <div className="text-slate-700 dark:text-slate-200 line-clamp-2">
                  {(c.summary_md || "").slice(0, 280)}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5">
                  {c.last_summarized_at
                    ? new Date(c.last_summarized_at).toLocaleDateString()
                    : ""}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
        </section>
      </div>
    </div>
  );
}

/** Compact left-rail people list for the dossier's wide layout.
 *  Loads top 80 people by interaction strength and highlights the
 *  current selection. Hidden on < lg by the caller. */
function PeopleSidePane({
  selectedId,
  onPick,
}: {
  selectedId: string | null;
  onPick: (id: string) => void;
}) {
  const [items, setItems] = useState<Person[]>([]);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api
      .peopleList({ sort: "strength", limit: 80 })
      .then((r) => setItems(r.people))
      .catch((e: Error) => setError(e.message));
  }, []);
  return (
    <nav
      aria-label="Other people"
      className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-2 space-y-0.5"
    >
      <div className="px-2 pt-1 pb-2 text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
        People
      </div>
      {error && (
        <div className="px-2 text-xs text-rose-600 dark:text-rose-300">{error}</div>
      )}
      <ul className="space-y-0.5 max-h-[70vh] overflow-y-auto">
        {items.map((p) => {
          const isActive = p.id === selectedId;
          const initial = (p.display_name || "?").slice(0, 1).toUpperCase();
          return (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => onPick(p.id)}
                aria-current={isActive ? "page" : undefined}
                className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-colors ${
                  isActive
                    ? "bg-violet-100 dark:bg-violet-900/30 text-violet-900 dark:text-violet-100"
                    : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/40"
                }`}
              >
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                    isActive
                      ? "bg-violet-500 text-white"
                      : "bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200"
                  }`}
                >
                  {initial}
                </span>
                <span className="truncate">{p.display_name}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
